'use client'

import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { CameraIcon, FilesIcon, X } from 'lucide-react'
import Zoom from 'react-medium-image-zoom'

import type { RouterOutputs } from '@package/api'
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  toast,
} from '@ui/web/components'
import {
  FileUpload,
  FileUploadDropzone,
  FileUploadItem,
  FileUploadItemDelete,
  FileUploadItemMetadata,
  FileUploadItemPreview,
  FileUploadList,
  FileUploadTrigger,
  typographyVariants,
} from '@ui/web/lib'
import { buildUrl, getInitials } from '@helper/utils/format'
import { useDetectDevice } from '@helper/web/hooks'
import cn from '@config/tailwind/cn'

import type { ScoreboardType } from '../index'
import { useSharedTRPC } from '../../../../context'
import { AreYouSureDialog } from '../../../../lib'
import { CameraModal } from '../../../../lib/in-class-finish-modal/camera-modal'

type ScoreboardItem = RouterOutputs['lesson']['getScoreboardData']['items'][number]

type ShowWorkModalProps = ScoreboardType & {
  open: boolean
  onOpenChange: (open: boolean) => void | React.Dispatch<React.SetStateAction<boolean>>
  childProfile: ScoreboardItem['child']
  data: Pick<ScoreboardItem, 'learnProgress'>
}

export function ShowWorkModal({ open, onOpenChange, childProfile, data, ...p }: ShowWorkModalProps) {
  const trpc = useSharedTRPC()
  const queryClient = useQueryClient()
  const device = useDetectDevice()

  const [files, setFiles] = useState<File[]>([])
  const [isCameraOpen, setIsCameraOpen] = useState(false)
  const [errorMsgFile, setErrorMsgFile] = useState<string | null>(null)
  const [openAreYouSureDialog, setOpenAreYouSureDialog] = useState(false)

  const currentLearnProgress = data.learnProgress

  function resetAndClose() {
    setFiles([])
    setErrorMsgFile(null)
    setIsCameraOpen(false)
    setOpenAreYouSureDialog(false)
    onOpenChange(false)
  }

  const addAttachmentMutation = useMutation(
    trpc.lesson.addInClassAttachment.mutationOptions({
      onSuccess: async () => {
        toast.success('Hasil kerja berhasil ditambahkan')
        await queryClient.invalidateQueries({
          queryKey: trpc.lesson.getScoreboardData.infiniteQueryKey(),
        })
        resetAndClose()
      },
      onError: () => {
        toast.error('Gagal menambahkan hasil kerja')
      },
    }),
  )

  const isUploading = addAttachmentMutation.isPending

  async function handleUpload() {
    if (!currentLearnProgress || files.length === 0) return
    setErrorMsgFile(null)

    try {
      const presignedResults = await Promise.all(
        files.map(async (file) => {
          const extension = (file.name.split('.').pop()?.toLowerCase() ?? 'bin').slice(0, 5)
          const res = await fetch(buildUrl('api', '/api/upload/presigned'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ fileName: file.name, contentType: file.type, bucket: 'content-private' }),
          })
          if (!res.ok) throw new Error(`Failed to get presigned URL for ${file.name}`)
          const { uploadUrl, key } = (await res.json()) as { uploadUrl: string; key: string }
          return { uploadUrl, key, extension, file, fileName: file.name, mimeType: file.type }
        }),
      )

      const uploadResults = await Promise.all(
        presignedResults.map(async ({ uploadUrl, file }) => {
          const res = await fetch(uploadUrl, {
            method: 'PUT',
            body: file,
            headers: { 'Content-Type': file.type },
          }).catch(() => null)
          return res?.ok ?? false
        }),
      )

      if (!uploadResults.every(Boolean)) {
        // Invalidate just in case some files are successfully uploaded
        await queryClient.invalidateQueries({
          queryKey: trpc.lesson.getScoreboardData.infiniteQueryKey(),
        })
        toast.error('Gagal mengunggah beberapa file')
        return
      }

      addAttachmentMutation.mutate({
        learnProgressId: currentLearnProgress.id,
        files: presignedResults.map((r) => ({
          key: r.key,
          fileName: r.fileName,
          extension: r.extension,
          mimeType: r.mimeType,
        })),
      })
    } catch {
      toast.error('Gagal mengunggah hasil kerja')
    }
  }

  if (!currentLearnProgress) return null

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(open) => {
          if (!open) {
            setFiles([])
            setErrorMsgFile(null)
            setIsCameraOpen(false)
            setOpenAreYouSureDialog(false)
          }

          onOpenChange(open)
        }}
      >
        <DialogContent className='srd:z-50 srd:max-w-[450px]'>
          <DialogHeader>
            <DialogTitle>Hasil kerja</DialogTitle>
            <DialogDescription className='srd:sr-only'>
              Lihat hasil kerja untuk pelajaran {currentLearnProgress.lesson?.title ?? ''}
            </DialogDescription>
          </DialogHeader>

          <div className='srd:flex srd:flex-col srd:gap-y-8 srd:w-full srd:items-center srd:justify-center srd:text-neutral-black'>
            <div className='srd:flex srd:flex-col srd:gap-y-2 srd:w-full'>
              <h6 className={cn(typographyVariants({ variant: 'title-lg' }))}>{currentLearnProgress.lesson?.title}</h6>

              <div className='srd:flex srd:items-center srd:gap-x-2 srd:flex-nowrap srd:shrink-0'>
                <Avatar className='srd:size-10'>
                  <AvatarImage src={childProfile.image ?? undefined} />
                  <AvatarFallback>{getInitials(childProfile.name)}</AvatarFallback>
                </Avatar>

                <div className='srd:flex srd:flex-col srd:gap-y-1'>
                  <p className={cn(typographyVariants({ variant: 'body-md' }))}>{childProfile.name}</p>

                  {childProfile.team && (
                    <p className={cn(typographyVariants({ variant: 'label-sm' }))}>{childProfile.team.name}</p>
                  )}
                </div>
              </div>
            </div>

            {currentLearnProgress.childSubmissions.length > 0 && (
              <div className='srd:w-full srd:flex srd:flex-col srd:gap-y-2'>
                <p className='srd:text-neutral-black srd:font-medium srd:text-left'>
                  Unggahan hasil kerja {p.from === 'web' ? 'anak' : 'murid'}
                </p>

                <div className='srd:flex srd:items-center srd:justify-center srd:w-full srd:flex-wrap'>
                  {currentLearnProgress.childSubmissions
                    .filter((submission) => submission.fileUrl)
                    .map((submission, index) => (
                      <div
                        key={submission.id}
                        className='srd:flex srd:size-20 srd:shrink-0 srd:items-center srd:justify-center srd:overflow-hidden srd:rounded srd:border srd:border-navy-20 relative'
                      >
                        <Zoom>
                          <img
                            alt={`submission-${index}`}
                            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                            src={submission.fileUrl!}
                            className='srd:size-full srd:object-cover'
                            decoding='async'
                            loading='lazy'
                          />
                        </Zoom>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {p.type === 'course' && currentLearnProgress.mainUserAttachments.length > 0 && (
              <div className='srd:w-full srd:flex srd:flex-col srd:gap-y-2'>
                <p className='srd:text-neutral-black srd:font-medium srd:text-left'>Unggahan hasil kerja guru</p>

                <div className='srd:flex srd:items-center srd:justify-center srd:w-full srd:flex-wrap'>
                  {currentLearnProgress.mainUserAttachments
                    .filter((attachment) => attachment.fileUrl)
                    .map((attachment, index) => (
                      <div
                        key={attachment.id}
                        className='srd:flex srd:size-20 srd:shrink-0 srd:items-center srd:justify-center srd:overflow-hidden srd:rounded srd:border srd:border-navy-20 relative'
                      >
                        <Zoom>
                          <img
                            alt={`attachment-${index}`}
                            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                            src={attachment.fileUrl!}
                            className='srd:size-full srd:object-cover'
                            decoding='async'
                            loading='lazy'
                          />
                        </Zoom>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {p.type === 'course' && currentLearnProgress.mainUserAttachments.length < 5 && (
              <div className='srd:w-full srd:flex srd:flex-col srd:gap-y-2'>
                <div className='srd:flex srd:items-center'>
                  <p className='srd:text-neutral-black srd:font-medium srd:flex srd:items-center'>
                    Hasil Kerja Tambahan
                  </p>
                  <Badge className='srd:bg-transparent srd:hover:bg-transparent srd:rounded-sm srd:text-navy-40 srd:border-navy-40 srd:border srd:ml-2'>
                    Opsional
                  </Badge>
                </div>

                <FileUpload
                  value={files}
                  onValueChange={setFiles}
                  maxFiles={Math.max(0, 5 - currentLearnProgress.mainUserAttachments.length)}
                  maxSize={5 * 1024 * 1024}
                  className='srd:w-full srd:max-w-md'
                  onFileReject={(file, message) =>
                    setErrorMsgFile(
                      `"${file.name.length > 20 ? `${file.name.slice(0, 20)}...` : file.name}" ditolak. ${message}`,
                    )
                  }
                  multiple
                  accept='image/*'
                >
                  <FileUploadDropzone className='srd:border-navy-50 srd:flex srd:flex-col srd:gap-y-6'>
                    <div className='srd:flex srd:flex-col srd:items-center srd:gap-4 srd:text-center'>
                      <p className='srd:text-sm srd:text-navy-100'>Drag & drop file atau</p>

                      <div className='srd:flex srd:items-center srd:justify-center srd:flex-wrap srd:gap-1'>
                        <FileUploadTrigger asChild>
                          <Button type='button' className='srd:rounded-full srd:bg-navy-20 srd:text-navy-100'>
                            <FilesIcon className='srd:w-4 srd:h-4 srd:mr-2 srd:text-navy-100' />
                            {device.isDesktop ? 'Pilih file' : 'Pilih file / Ambil foto'}
                          </Button>
                        </FileUploadTrigger>

                        {device.isDesktop && (
                          <Button
                            type='button'
                            className='srd:rounded-full srd:bg-navy-20 srd:text-navy-100'
                            onClick={(e) => {
                              e.stopPropagation()
                              setIsCameraOpen(true)
                            }}
                          >
                            <CameraIcon className='srd:w-4 srd:h-4 srd:mr-2 srd:text-navy-100' />
                            Ambil foto
                          </Button>
                        )}
                      </div>

                      <p className='srd:text-navy-40 srd:text-xs'>
                        {`Sisa ${Math.max(0, 5 - currentLearnProgress.mainUserAttachments.length)} dari 5 file, maksimal 5MB per file.`}
                      </p>
                    </div>
                  </FileUploadDropzone>
                  <FileUploadList orientation='horizontal'>
                    {files.map((file, index) => (
                      <FileUploadItem key={index} value={file} className='srd:p-0'>
                        <FileUploadItemPreview className='srd:size-20' />

                        <FileUploadItemMetadata className='srd:sr-only' />

                        <FileUploadItemDelete asChild>
                          <Button
                            variant='secondary'
                            size='icon'
                            className='srd:-top-1 srd:-right-1 srd:absolute srd:size-5 srd:rounded-full'
                          >
                            <X className='srd:size-3' />
                          </Button>
                        </FileUploadItemDelete>
                      </FileUploadItem>
                    ))}
                  </FileUploadList>
                </FileUpload>

                {errorMsgFile && <p className='srd:text-error srd:text-sm srd:text-left'>{errorMsgFile}</p>}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant='outline' onClick={() => onOpenChange(false)} type='button'>
              Tutup
            </Button>

            {p.type === 'course' && currentLearnProgress.mainUserAttachments.length < 5 && (
              <Button onClick={handleUpload} loading={isUploading} disabled={isUploading || files.length === 0}>
                Tambah hasil kerja
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CameraModal
        open={isCameraOpen}
        onOpenChange={setIsCameraOpen}
        onUpload={(file) => {
          setFiles((prev) => [...prev, file])
          setIsCameraOpen(false)
        }}
      />

      <AreYouSureDialog open={openAreYouSureDialog} onOpenChange={setOpenAreYouSureDialog} onConfirm={resetAndClose} />
    </>
  )
}
